import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { PriceWithSchema } from '@ynap/price';
import { TransitionSingleBadge, SingleBadge } from '@ynap/badge';
import { CombinedImage } from '@ynap/image';
import BEMHelper from '@ynap/bem-helper';
import { withOptimizely } from '@ynap/optimizely-utils';

const bem = new BEMHelper('ProductItem');

const ProductSummary = (props) => {
    const {
        product,
        messages,
        modifier,
        loading,
        className,
        nameModifier,
        productImageAspectRatio,
        imageSrcsetSize,
        children,
        detailedView = true,
        cropDetails = true,
        withIntersectionObserver = true,
        withSkeleton = true,
        locale,
        hasDotAlert = false,
        type = '',
        ImageComponent,
        hidePrice = false,
        QuickAddButton,
        optimizely,
        doubleImageWithoutPadding,
        doubleImageModifier,
        isWishlist = false,
        listingPage,
        country,
        details,
        omnibusAlignment,
        searchResults,
        CTABtn,
        imageOverlayElement,
        ...rest
    } = props;

    const { imgSrc, designerName = '', name = '', partNumber, price, badge, onSale, consideredBadge, saleExclusive } = product;
    const displayable = Object.prototype.hasOwnProperty.call(product, 'displayable') ? product.displayable : true; // Products with no 'displayable' property should default to true
    const productIsUnavailable = !displayable;
    const unavailableProduct = productIsUnavailable ? 'unavailableProduct' : '';
    const skeleton = loading ? 'skeleton' : null;
    const quickAdd = QuickAddButton ? 'quickAdd' : '';
    const modifiers = modifier ? { skeleton, unavailableProduct, [modifier]: true } : { skeleton, unavailableProduct };
    const imgAltString = `${designerName ? designerName : ''}${name ? ' ' + name : ''}`;

    return (
        <div className={bem(null, null, className)} {...rest}>
            <div className={bem('p', [type, modifier])}>
                <meta itemProp="productID" content={partNumber} /> {/* product info for personal shopper chrome extension */}
                <div className={bem('imageContainer', [!displayable ? 'imgModifier' : '', type, quickAdd])}>
                    <div
                        onClick={() => {
                            (QuickAddButton && optimizely?.track('recommendation_image_click')) || optimizely?.track('recommendations_pids_ctr');
                        }}
                    >
                        <CombinedImage
                            src={imgSrc?.primary}
                            secondarySrc={QuickAddButton ? undefined : imgSrc?.secondary}
                            alt={imgAltString}
                            loading={loading}
                            yOffset={'0%'}
                            aspectRatio={productImageAspectRatio}
                            imageSrcsetSize={imageSrcsetSize}
                            primarySrcset={imgSrc?.primarySrcset}
                            primaryPlpSrcset={imgSrc?.primaryPlpSrcset}
                            secondarySrcset={imgSrc?.secondarySrcset}
                            secondaryPlpSrcset={imgSrc?.secondaryPlpSrcset}
                            displayError={!(loading || imgSrc?.primary)}
                            withIntersectionObserver={withIntersectionObserver}
                            withSkeleton={withSkeleton}
                            hideSecondaryOnMobile={true}
                            ImageComponent={ImageComponent}
                            doubleImageWithoutPadding={doubleImageWithoutPadding}
                            doubleImageModifier={doubleImageModifier}
                            imageOverlayElement={imageOverlayElement}
                        />
                    </div>
                    {QuickAddButton && <QuickAddButton />}
                </div>
                <div className={bem('skeletonContainer', [skeleton, unavailableProduct, modifier, type])}>
                    {CTABtn}

                    <div className={bem('content', modifiers)}>
                        {loading ? null : (
                            <>
                                {designerName && (
                                    <span className={bem('details', [cropDetails ? 'brief' : '', modifier])} data-product-id={partNumber}>
                                        <span dir="ltr" data-testid="pid-summary-designer" className={bem('designer', modifier)}>
                                            {designerName}
                                        </span>
                                        {/* Line break added to make -line-clamp behave */}
                                        {name && (
                                            <>
                                                <span className={bem('lineBreakHack', modifier)}>
                                                    <br />
                                                </span>
                                                <span data-testid="pid-summary-description" className={bem('name', nameModifier)}>
                                                    {name}
                                                </span>
                                            </>
                                        )}
                                    </span>
                                )}

                                {children}

                                {productIsUnavailable && <div className={bem('message')}>{messages.product.unavailableProduct()}</div>}

                                {displayable && (
                                    <>
                                        {detailedView && !hidePrice && (
                                            <PriceWithSchema
                                                {...price}
                                                messages={messages}
                                                className={bem('price', modifier)}
                                                locale={locale}
                                                priceSuffix={messages.price?.taxIncluded?.()}
                                                isWishlist={isWishlist}
                                                listingPage={listingPage}
                                                designerName={product?.designerName}
                                                badgeKey={product?.badge?.key}
                                                country={country}
                                                details={details}
                                                omnibusAlignment={omnibusAlignment}
                                                searchResults={searchResults}
                                                saleExclusive={saleExclusive}
                                            />
                                        )}
                                        {detailedView && (
                                            <TransitionSingleBadge className={bem('badge', modifier)} hasDotAlert={hasDotAlert} onSale={onSale} badge={badge} />
                                        )}
                                        {consideredBadge && (
                                            <>
                                                <SingleBadge
                                                    className={bem('consideredBadge')}
                                                    considered={true}
                                                    badge={{ label: consideredBadge, key: 'CONSIDERED_BADGE' }}
                                                    translation={messages.considered.badge()}
                                                />
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

ProductSummary.propTypes = {
    product: PropTypes.object.isRequired,
    price: PropTypes.object,
    messages: PropTypes.shape({
        product: PropTypes.shape({
            onSale: PropTypes.func.isRequired,
            unavailableProduct: PropTypes.func.isRequired,
        }).isRequired,
        price: PropTypes.shape({
            taxIncluded: PropTypes.func,
        }),
        considered: PropTypes.shape({
            badge: PropTypes.func.isRequired,
        }),
    }).isRequired,
    modifier: PropTypes.string,
    nameModifier: PropTypes.string,
    loading: PropTypes.bool,
    className: PropTypes.string,
    children: PropTypes.node,
    detailedView: PropTypes.bool,
    cropDetails: PropTypes.bool,
    productImageAspectRatio: PropTypes.string,
    imageSrcsetSize: PropTypes.string,
    withIntersectionObserver: PropTypes.bool,
    hasDotAlert: PropTypes.bool,
    locale: PropTypes.string.isRequired,
    country: PropTypes.string.isRequired,
    withSkeleton: PropTypes.bool,
    hidePrice: PropTypes.bool,
    type: PropTypes.string,
    ImageComponent: PropTypes.elementType,
    QuickAddButton: PropTypes.elementType,
    optimizely: PropTypes.shape({
        track: PropTypes.func.isRequired,
    }),
    doubleImageWithoutPadding: PropTypes.string,
    doubleImageModifier: PropTypes.string,
    details: PropTypes.bool,
    omnibusAlignment: PropTypes.bool,
    searchResults: PropTypes.bool,
    listingPage: PropTypes.bool,
    isWishlist: PropTypes.bool,
    CTABtn: PropTypes.elementType,
    recommendedProduct: PropTypes.object,
    imageOverlayElement: PropTypes.node,
};

export const areEqual = (prevProps, nextProps) => {
    if (prevProps.product.partNumber !== nextProps.product.partNumber) {
        return false;
    }
    if (prevProps.loading !== nextProps.loading || prevProps.QuickAddButton !== nextProps.QuickAddButton) {
        return false;
    }
    if (prevProps.CTABtn !== nextProps.CTABtn) {
        return false;
    }
    // badges might update when user data is fetched and segmented badges are shown
    if (prevProps.product.badge?.key !== nextProps.product.badge?.key) {
        return false;
    }

    //update image on hover over a colour
    if (prevProps.colourImagePartNumber !== nextProps.colourImagePartNumber) {
        return false;
    }

    return true;
};

export default memo(withOptimizely(ProductSummary), areEqual);
